import { useEffect, useState } from "react";

const STORAGE_KEY = "fc-pwa-install-dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports as Mac, but has touch — refine
  const iPadOS =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOS;
}

/**
 * usePwaInstall — exposes install state for the FC Staff PWA install banner.
 * Returns:
 *   visible:   should we render the banner?
 *   platform:  "android" | "ios" | null  — drives Install vs. Instructions UI
 *   promptInstall(): triggers native Android prompt
 *   dismiss(): hides banner permanently for this device/browser
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const standalone = isStandalone();
  const ios = isIOS();

  // Decide platform
  let platform = null;
  if (deferred) platform = "android";
  else if (ios && !standalone) platform = "ios";

  const visible =
    !standalone && !installed && !dismissed && platform !== null;

  const promptInstall = async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice?.outcome === "accepted") {
      setInstalled(true);
    } else {
      // User cancelled the native prompt — treat as dismiss so we don't nag
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
      setDismissed(true);
    }
    return choice;
  };

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setDismissed(true);
  };

  return { visible, platform, promptInstall, dismiss };
}
